/**
 * Bringing a Cardmarket inventory in.
 *
 * THE SERVER DECIDES. This screen uploads a file and renders the answer.
 *
 * The other design was a browser-side parse with the rows reviewed in a table
 * before anything was sent, and it was considered and rejected. Two reasons it
 * would have been worse than it looks: the rules that decide whether a row can
 * become a listing live in `api/src/lib/cardmarket-import.ts` — eighty-five
 * language spellings, four price formats, the catalogue join — and a second
 * copy of them here would be free to disagree with the first, silently, in the
 * direction of "looked fine on screen, refused on upload". And the catalogue
 * join cannot be done in a browser at all: `idProduct` has to be looked up
 * against `catalog_product`, which is where `unknown_product` comes from.
 *
 * So there is exactly one implementation of what a row means, it is the one
 * that writes the rows, and this file has no opinion about CSV whatsoever.
 *
 * LOOK FIRST, THEN COMMIT — AND THE PREVIEW IS THE IMPORT.
 *
 * Choosing a file uploads it with `?dryRun=1`. The server runs the whole import
 * — parse, language match, catalogue join, dedupe, inserts — and then ROLLBACKs
 * instead of committing, and answers in the same shape. So the review step is
 * not a model of what an import would do; it is what the import did, undone.
 * A model written separately here would be free to drift, and the day it said
 * nine and the real run managed eight, nothing on this screen could say which
 * of the two was lying.
 *
 * Confirming sends the same file again with the dry run off. The file is still
 * in the browser, so there is nothing half-made on the server to refer back to —
 * which is the same property that makes the preview safe.
 *
 * AND A PREVIEW IS NOT A PROMISE. The result replaces it, always, even when the
 * two disagree; where they do, the screen says so rather than letting the older
 * numbers stand. `ImportReport` renders both, in one component with one tense
 * switch, precisely so that "what will happen" and "what happened" cannot drift
 * into saying different things about the same file.
 *
 * WHAT THE FOUR NUMBERS MEAN, AND WHY THEY ARE NEVER ADDED UP.
 *
 * `imported`, `revived`, `skipped` and `failed` are four different events and
 * the copy here keeps them apart on purpose:
 *
 *   imported — became a listing that did not exist before.
 *   revived  — was here, taken down, and has gone back up with the file's price
 *              and quantity. Nothing was created; something WAS rewritten, and
 *              that is the half a trader has to be told about.
 *   skipped  — was already here, and nothing about it changed. This is the
 *              re-import working correctly, not a problem to solve.
 *   failed   — could not be read, and is not listed. Something to fix.
 *
 * "3 imported" on its own, next to rows that were quietly dropped, is the
 * failure this screen exists to prevent — so a failure is never folded into a
 * total and never phrased as a warning about "some rows". `revived` is apart
 * from `imported` for the mirror of that reason: a count of six "imported" over
 * three new cards and three repriced ones would hide the repricing entirely.
 *
 * WHAT IS NOT HERE.
 *
 * Wants. The mockup this took its look from imports wants as a first-class
 * mode; our API does not support it in any form, and the extension writes a
 * different file for them with different columns — which the server recognises
 * and names. A button leading to an import that cannot happen is worse than no
 * button, so there is none, and the panel below says out loud that the wants
 * file is the wrong one.
 */
import React from "react";
import { api } from "./api.jsx";
import { useApp } from "./store.jsx";
import { BackLink, ModalHost, SectionHead, StatRow, Dialog } from "./chrome.jsx";

const { Button, Card, Icon, FluteDivider, Select } = window.CardboardmarketDesignSystem_2a3239;

/**
 * The columns the server refuses to proceed without, spelled exactly as the
 * extension writes them.
 *
 * A copy of `REQUIRED_COLUMNS` in api/src/lib/cardmarket-import.ts, and it is a
 * copy on purpose rather than a fetched value: it is shown BEFORE anybody
 * uploads anything, so that somebody holding the wrong file can see it is the
 * wrong file without spending an upload to find out. If the server's list ever
 * changes, what happens here is that this panel is out of date — not that an
 * import breaks — and the 422 still names the missing columns exactly.
 */
const EXPECTED_HEADER = "ArticleID;idProduct;Language;Condition;Price_EUR;Amount";

/**
 * The same for the wants export: a copy of `REQUIRED_WANT_COLUMNS` in
 * api/src/lib/wants-import.ts, for the same reason, so change the two
 * together. The server's own list, not the whole export's header — the panel
 * says "whatever else it carries", and these are what it must.
 */
const EXPECTED_WANTS_COLUMNS = "WantListName;idProduct";

/**
 * Why a row did not become a listing, in words.
 *
 * Keyed by the reason codes in `FailureReason` and `SkipReason`. Named
 * `_NAMES` deliberately: `check-translations.mjs` treats a `*_NAMES` table read
 * through `t()` as translatable and requires Spanish for every value in it, so
 * adding a reason here without translating it fails a check rather than
 * quietly rendering English to a Spanish reader.
 *
 * The code itself is the fallback, so a reason added to the API before it is
 * added here shows `unknown_something` rather than nothing at all — ugly, and
 * still more use than a blank cell.
 */
const REASON_NAMES = {
  // --- failures: something to fix ---
  missing_article_id: "The row carries no Cardmarket article number",
  invalid_article_id: "The Cardmarket article number is not a number",
  missing_product_id: "The row carries no Cardmarket product number",
  invalid_product_id: "The Cardmarket product number is not a number",
  unknown_product: "This card is not in our catalogue",
  // Sealed product, or an online code: real catalogue rows, never listed here.
  product_not_listable: "Not a physical card: sealed product or an online code",
  unsupported_game: "A game this market does not trade in",
  missing_language: "The row carries no language",
  unknown_language: "A language we do not recognise",
  ambiguous_language: "A language that could be more than one",
  missing_condition: "The row carries no condition",
  unknown_condition: "A condition we do not recognise",
  missing_price: "The row carries no price",
  zero_price: "A price of zero",
  invalid_price: "A price we could not read",
  invalid_quantity: "An amount we could not read",
  quantity_too_large: "More copies than one listing can hold",
  // --- skips: the re-import working correctly ---
  already_imported: "Already listed, and left exactly as it was",
  already_traded: "Already traded, and that record stays as it is",
  // Wanted already, which is a re-import of a wants list working correctly.
  already_wanted: "Already on your wants list, and left as it is",
  // "Card" rather than "article": the stock file repeats an article and the
  // wants file repeats a CARD across decks, and one sentence covers both.
  duplicate_in_file: "The same card appears twice in the file",
};

/** Reasons that mean "already here" rather than "could not be read". */
const SKIP_REASONS = new Set(['already_imported', 'already_traded', 'already_wanted', 'duplicate_in_file']);

/**
 * The file, chosen by dropping it or by picking it.
 *
 * The input is hidden and driven by a real button, the same way the photo
 * manager does it: a bare file input renders as "Choose file / no file chosen",
 * which says nothing about what to choose, and cannot be styled to say it.
 *
 * Drag and drop is the other half rather than the whole thing. It does not
 * exist on a phone, and this product is used on phones.
 */
function DropZone({ file, onFile, disabled }) {
  const { t } = useApp();
  const inputRef = React.useRef(null);
  const [over, setOver] = React.useState(false);

  const take = (files) => {
    const chosen = files && files[0];
    if (chosen) onFile(chosen);
  };

  return (
    <div
      className={'cbm-drop' + (over ? ' cbm-drop--over' : '')}
      /*
       * `preventDefault` on dragOver is what makes a drop possible at all: the
       * browser's default for a dragged file is to NAVIGATE to it, so without
       * this the page is replaced by the CSV and the import screen is gone.
       */
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (!disabled) take(e.dataTransfer && e.dataTransfer.files);
      }}
    >
      <Icon name="upload" size={28} />
      {file ? (
        <>
          {/* The name is the file's own, so it is not translated and not
              styled as prose — it is what the person recognises. */}
          <span style={{ font: 'var(--type-body)', color: 'var(--text-strong)', wordBreak: 'break-all' }}>{file.name}</span>
          <Button variant="ghost" size="sm" disabled={disabled}
            onClick={() => inputRef.current && inputRef.current.click()}>{t("Choose a different file")}</Button>
        </>
      ) : (
        <>
          <span style={{ font: 'var(--type-body)', color: 'var(--text-strong)' }}>{t("Drop your stock export here")}</span>
          <Button variant="outline" disabled={disabled}
            onClick={() => inputRef.current && inputRef.current.click()}>{t("Choose a file")}</Button>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: 'none' }}
        onChange={(e) => {
          take(e.target.files);
          // Cleared, or choosing the same file twice in a row fires no change
          // event and the second attempt appears to do nothing.
          e.target.value = '';
        }}
      />
    </div>
  );
}

/**
 * What the file is supposed to look like, for the source being imported: its
 * `columns` in `IMPORT_SOURCE_FACTS`, and the `FILE_SHAPE_*_NAMES` sentences.
 *
 * Shown before the upload rather than after a refusal, because the commonest
 * wrong file is the OTHER export — same extension, same button, different
 * columns — and somebody looking at both in a downloads folder cannot tell them
 * apart without opening one.
 */
function FileShape({ kind }) {
  const { t } = useApp();
  return (
    <Card padding="var(--space-6)" tone="sunken" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <span className="cbm-label">{t("What the file looks like")}</span>
      <p style={{ margin: 0, font: 'var(--type-body-sm)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
        {t(FILE_SHAPE_IS_NAMES[kind])}
      </p>
      <div className="cbm-code">{IMPORT_SOURCE_FACTS[kind].columns}</div>
      <p style={{ margin: 0, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
        {t(FILE_SHAPE_OTHER_NAMES[kind])}
      </p>
    </Card>
  );
}

/**
 * One group of the report: a reason, how many rows, and the offending values.
 *
 * The values are the point. A trader with six hundred Dutch cards needs to see
 * the word we did not recognise — once — so they can tell us what it means;
 * "unknown language: 600 rows" with the word withheld is a dead end.
 */
function ProblemRows({ problems, preview }) {
  const { t } = useApp();
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="cbm-imp-table">
        <thead>
          <tr>
            {/* The tense follows the report it belongs to. A preview's table
                heading saying "what happened" over rows that have not happened
                is the small lie the whole review step exists to avoid. */}
            <th>{preview ? t("What will happen") : t("What happened")}</th>
            <th style={{ textAlign: 'right' }}>{t("Rows")}</th>
            <th>{t("In your file")}</th>
          </tr>
        </thead>
        <tbody>
          {problems.map((p) => (
            <tr key={p.reason}>
              <td>
                {t(REASON_NAMES[p.reason] || p.reason)}
                {/* Which half this belongs to, said on the row rather than left
                    to the reader to remember from the paragraph above. */}
                <span style={{ display: 'block', font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
                  {SKIP_REASONS.has(p.reason)
                    ? t("Nothing to do")
                    : preview ? t("Will not be listed") : t("Not listed")}
                </span>
              </td>
              <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{p.count}</td>
              <td>
                {p.values.length ? (
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', wordBreak: 'break-word' }}>
                    {p.values.join(', ')}
                    {p.moreValues ? ' ' + t("and {n} more", { n: p.moreValues }) : ''}
                  </span>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The answer, rendered honestly — before it happens and after it happened.
 *
 * Three separate sentences rather than one summary, because the three numbers
 * mean different things and a sentence that averages them is the lie this
 * screen is written to avoid. Each appears only when it is not zero: an import
 * where nothing failed should not have a line about failures at all.
 *
 * ONE COMPONENT FOR BOTH STEPS, with `preview` switching the tense. Two would
 * have been easier to write and is the version that goes wrong: the review and
 * the result are the same numbers about the same file, and the moment they are
 * rendered by two components they are free to disagree about what a skip is, or
 * to keep a group the other one drops. The tense is the only difference there
 * is, so the tense is the only thing that differs here.
 *
 * `promised` is the preview this result replaced, and it is used for exactly one
 * thing: saying so when the two disagree. Everything else on a result comes from
 * the result. A preview is not a promise, and nothing here treats it as one.
 */
function ImportReport({ report, preview, promised, busy, onConfirm, onAgain, kind }) {
  /*
   * WHICH NOUN THIS SCREEN IS ABOUT. The two imports share every step — choose,
   * preview, confirm, report — and differ in one word that appears a dozen
   * times. Reusing the screen and leaving it saying "listing" over a wants
   * import is the kind of wrongness that reads as somebody else's bug report.
   */
  const wants = kind === 'wants';
  const { go, notify, trader, t } = useApp();
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  /*
   * Did the import do what the preview said it would?
   *
   * It usually does — the preview IS the import, rolled back — but it is not a
   * guarantee and must never be presented as one: minutes can pass on the review
   * step, and the same file imported from another tab in between would move the
   * skip count. When they differ, the screen says which of the two is true
   * rather than leaving somebody to notice that a number changed.
   */
  const differsFromPreview = !preview && promised
    && (promised.imported !== report.imported
      || promised.revived !== report.revived
      || promised.skipped !== report.skipped
      || promised.failed !== report.failed);

  /**
   * The confirm, naming what it is about to do.
   *
   * "Import" alone under a report is a button somebody presses to find out what
   * it does. The number is already on the screen above it; putting it in the
   * verb is what makes the click a decision rather than a guess.
   *
   * Zero keeps the bare word. There is still something to press — the answer to
   * "what happens if I go ahead" is "nothing", and it is better learned from a
   * result that says so than from a step with no way forward.
   */
  /*
   * Cards that will be live afterwards, whether they are new or coming back.
   *
   * A revival is counted apart everywhere else on this screen, because it
   * rewrote something rather than creating it. Here the question is different —
   * "what am I about to press" — and the answer to that is the same for both:
   * a card that was not on sale is about to be. A button reading "Import"
   * over a file whose nine rows are all revivals would be the one place this
   * screen let somebody press something without saying what it does.
   */
  /*
   * `revived` is a STOCK idea and is absent from a wants answer, so it is
   * coalesced rather than added blind: `imported + undefined` is NaN, and the
   * button read "Import NaN cards" for every wants import until a browser test
   * failed to find it. A number is not something to assume is there because the
   * other endpoint sends one.
   */
  const willBeListed = report.imported + (report.revived ?? 0);
  const confirmLabel = willBeListed === 0
    ? t("Import")
    : willBeListed === 1
      ? t("Import one card")
      : t("Import {n} cards", { n: willBeListed });

  /*
   * The report we send is the SERVER's, verbatim.
   *
   * `report` is rendered by `reportText()` next to the code that made the
   * decisions, in English, addressed to us, and already guaranteed to fit the
   * four thousand characters `feedback.message` allows. Composing our own here
   * would be a client's paraphrase of somebody else's reasoning, and it would
   * be the paraphrase we then tried to debug from.
   *
   * `kind` is 'bug' because the feedback table's CHECK allows exactly
   * bug/idea/other and a failed import is the first of those.
   */
  const tellUs = async () => {
    setSending(true);
    try {
      await api.sendFeedback({
        message: report.report,
        kind: 'bug',
        page: (window.location.pathname + window.location.search).slice(0, 400),
      });
      setSent(true);
      notify(t("Thank you — we have it."));
    } catch (e) {
      notify(e && e.code === 'offline'
        ? t("You appear to be offline. Nothing was sent.")
        : t("That did not send. Try again in a moment."));
    } finally { setSending(false); }
  };

  return (
    <Card padding="var(--space-6)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      {/*
        Said above the numbers, not below them.

        Somebody reading "4" beside "listed" has already drawn a conclusion by
        the time they reach a caveat underneath it. The one sentence that changes
        what those four numbers mean has to arrive first.
      */}
      {preview ? (
        <p style={{ margin: 0, font: 'var(--type-body)', color: 'var(--text-strong)' }}>
          {t("Nothing has been imported yet. This is what will happen if you go ahead.")}
        </p>
      ) : null}
      {differsFromPreview ? (
        <p role="status" style={{ margin: 0, font: 'var(--type-body)', color: 'var(--text-body)' }}>
          {t("That is not quite what the preview said. What you are looking at now is what actually happened.")}
        </p>
      ) : null}
      {/*
        THE SAME ROW DESCRIBING TWO DIFFERENT IMPORTS, which is where this went
        wrong. "Will go back up" is about a listing that was taken down and is
        being relisted — a stock idea with no meaning for a want, and the wants
        endpoint accordingly sends no `revived` at all.

        So the stat rendered with an UNDEFINED value: the label slid up into the
        row where the numbers live and sat a line above its neighbours. Reported
        as a misalignment, which is what it looked like; it was a column that
        should not have been there.

        `wants` was already in scope five lines up and doing this job for every
        sentence below. Only the numbers had been left speaking about stock.
      */}
      <StatRow items={[
        { label: t("Rows read"), value: report.total },
        { label: wants
            ? (preview ? t("Will be saved") : t("Now saved"))
            : (preview ? t("Will be listed") : t("Now listed")),
          value: report.imported },
        /*
         * Relisted, beside listed and never inside it.
         *
         * These rows created nothing — same card, same listing, same URL — and
         * they REWROTE a price and a quantity that were already here. Adding
         * them to "now listed" would hide the second half, which is the half a
         * trader has to know about. Shown even at zero, like the two beside it,
         * so the four numbers stay a partition of "rows read".
         *
         * Absent for wants: there is nothing to put back up, and a zero would
         * be an answer to a question nobody asked.
         */
        ...(wants ? [] : [
          { label: preview ? t("Will go back up") : t("Back up"), value: report.revived },
        ]),
        /*
          A SYNC'S TWO NUMBERS, and only when there was a sync.
          
          Shown at zero inside one, because "0 will come down" is the answer a
          shop most wants to read before confirming — and never shown outside
          one, where they would be two permanent zeros answering a question
          nobody asked.
        */
        ...(report.updated || report.withdrawn || report.mode === 'sync' ? [
          { label: preview ? t("Will be rewritten") : t("Rewritten"), value: report.updated || 0 },
          { label: preview ? t("Will come down") : t("Came down"), value: report.withdrawn || 0 },
        ] : []),
        { label: t("Already here"), value: report.skipped },
        { label: t("Not read"), value: report.failed },
      ]} />
      <FluteDivider />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {report.total === 0 ? (
          <p style={{ margin: 0, font: 'var(--type-body)', color: 'var(--text-body)' }}>
            {t("That file has no rows in it.")}
          </p>
        ) : null}
        {report.imported ? (
          <p style={{ margin: 0, font: 'var(--type-body)', color: 'var(--text-body)' }}>
            {preview
              ? report.imported === 1
                ? (wants ? t("One row will become a want.") : t("One row will become a listing."))
                : (wants
                  ? t("{n} rows will become wants.", { n: report.imported })
                  : t("{n} rows will become listings.", { n: report.imported }))
              : report.imported === 1
                ? (wants ? t("One row is now a want.") : t("One row is now a listing."))
                : (wants
                  ? t("{n} rows are now wants.", { n: report.imported })
                  : t("{n} rows are now listings.", { n: report.imported }))}
          </p>
        ) : null}
        {/*
          The rows that were taken down and are coming back.

          Its own sentence, with the price and the quantity named in it. This is
          the one outcome of an import that CHANGES something that was already
          here, and a trader who withdrew a card, repriced it on Cardmarket and
          re-uploaded has to be told which of the two prices they are about to
          be selling at — before they press the button, which is why the preview
          says it in the future tense rather than only reporting it afterwards.
        */}
        {report.revived ? (
          <p style={{ margin: 0, font: 'var(--type-body)', color: 'var(--text-body)' }}>
            {preview
              ? report.revived === 1
                ? t("One row is here but taken down. It will go back up, with this file's price and quantity.")
                : t("{n} rows are here but taken down. They will go back up, with this file's price and quantity.", { n: report.revived })
              : report.revived === 1
                ? t("One row that was taken down is back up, with this file's price and quantity.")
                : t("{n} rows that were taken down are back up, with this file's price and quantity.", { n: report.revived })}
            {' '}
            {t("Each keeps the listing it had, so its link and any trade on it still work.")}
          </p>
        ) : null}
        {preview && willBeListed === 0 && report.total ? (
          /* The one thing a result never has to say, because a result with
             nothing in it is simply a result. Ahead of the confirm it is the
             answer to "what am I about to press", and it should not have to be
             worked out from a zero in a row of four numbers. */
          <p style={{ margin: 0, font: 'var(--type-body)', color: 'var(--text-body)' }}>
            {t("Nothing in this file is new. Going ahead will not add anything.")}
          </p>
        ) : null}
        {report.skipped ? (
          <p style={{ margin: 0, font: 'var(--type-body)', color: 'var(--text-body)' }}>
            {preview
              ? report.skipped === 1
                ? t("One row is already here, and will be left exactly as it is.")
                : t("{n} rows are already here, and will be left exactly as they are.", { n: report.skipped })
              : report.skipped === 1
                ? t("One row was already here, and nothing about it changed.")
                : t("{n} rows were already here, and nothing about them changed.", { n: report.skipped })}
            {' '}
            {t("Importing the same file twice never doubles an inventory, and never rewrites a price on a listing that is still up.")}
          </p>
        ) : null}
        {report.failed ? (
          <p style={{ margin: 0, font: 'var(--type-body)', color: 'var(--text-body)' }}>
            {preview
              ? report.failed === 1
                ? t("One row cannot be read, and will not be listed.")
                : t("{n} rows cannot be read, and will not be listed.", { n: report.failed })
              : report.failed === 1
                ? t("One row could not be read, and is not listed.")
                : t("{n} rows could not be read, and are not listed.", { n: report.failed })}
            {' '}
            {preview
              ? t("Nothing is guessed: a row we cannot read is left out rather than filled in.")
              : t("Nothing was guessed: a row we cannot read is left out rather than filled in.")}
          </p>
        ) : null}
      </div>
      {report.problems && report.problems.length
        ? <ProblemRows problems={report.problems} preview={preview} /> : null}
      <div className="cbm-actionbar">
        {preview ? (
          <>
            <Button size="lg" disabled={busy} onClick={onConfirm}>
              {busy ? t("Importing…") : confirmLabel}
            </Button>
            <Button variant="outline" disabled={busy} onClick={onAgain}>
              {t("Choose a different file")}
            </Button>
          </>
        ) : (
          <>
            {/*
              WHERE THE THINGS YOU JUST IMPORTED ACTUALLY ARE.
              This said "See your listings" and opened the Selling tab after a
              WANTS import — the wrong words and the wrong destination, leaving
              somebody who had just brought over 249 wants looking at a list of
              cards for sale. Same root as the numbers above it: the wants flow
              borrowing the inventory's vocabulary.
            */}
            {/*
              AND A SHOP'S LISTINGS ARE NOT ON /you.
              Reported: this button "should take a shop to their stock page".
              Right — a shop account is sealed into its back office, so sending
              it to the trading side's profile either bounces or lands on the
              dashboard's default section. Either way somebody who has just
              imported three hundred cards is not looking at them, which is the
              one thing this button exists to do.

              The same bug as the wants destination noted above, one account
              kind along: the button knew where ONE sort of importer keeps their
              things.
            */}
            {willBeListed ? (
              <Button onClick={() => (trader && trader.kind === 'shop'
                ? go('dashboard', { sec: 'stock' })
                : go('you', wants ? { tab: 'wants' } : undefined))}>
                {wants ? t("See your wants") : t("See your listings")}
              </Button>
            ) : null}
            <Button variant="outline" onClick={onAgain}>{t("Import another file")}</Button>
            {/*
              Only offered where something actually failed, and only on a result.
              On a clean import there is nothing to report, and a "tell us" button
              under a green result invites reports about nothing. On a preview it
              would send us a ticket about an import that has not happened yet —
              and the trader is one click from making it happen or not.
            */}
            {report.failed ? (
              <Button variant="ghost" iconRight="send" disabled={sending || sent} onClick={tellUs}>
                {sent ? t("Sent") : sending ? t("Sending…") : t("Tell us about this")}
              </Button>
            ) : null}
          </>
        )}
      </div>
      {report.failed && !preview ? (
        <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {t("That sends us the reasons and the values above, and nothing else out of your file.")}
        </span>
      ) : null}
    </Card>
  );
}

/**
 * Which import, chosen before the file.
 *
 * One option today, and the step stays anyway. Cardmarket is how an inventory
 * arrives NOW, not what importing means: the owner's design put this step in
 * deliberately, on the stated grounds that other ways in are expected later, and
 * a screen that hardcodes today's only answer is one that has to be rebuilt
 * rather than extended when the second one appears.
 *
 * It is also why nothing outside this file says "Cardmarket" to a trader. The
 * button on Selling offers to import an inventory; which shop it came from is a
 * detail of the answer, not of the question.
 *
 * A list of one reads as a list, not as a wizard step to click past — the card
 * carries what the file is and where it comes from, which somebody arriving here
 * for the first time does not know yet.
 */
/**
 * How to get the file, for somebody who has never exported one.
 *
 * Behind a link rather than on the card. The card's job is to say what this
 * source IS, so somebody can decide they want it; warning them about a browser
 * extension before they have decided is answering a question nobody asked yet.
 *
 * The honest reassurance is NOT "trust the extension" — we did not write it and
 * cannot vouch for it. It is that OUR side never touches their Cardmarket
 * account: they export a file, they choose it here, and no login of theirs comes
 * near us. That is true, it is what somebody hesitating actually wants to know,
 * and it does not ask them to take anything on faith.
 *
 * The version note is not padding, and it is ONE number for both sources: the
 * first version that gets both exports right, so a trader who imports both
 * never meets two minimums. For stock that would be v2.2.13: before it the
 * extension's language list stopped at 11, so cards in the other six exported
 * with a BLANK language — and its own bulk update then wrote them back to
 * Cardmarket as German. An old version does not merely import badly here; it
 * can mislabel their stock there. Read out of the extension's source, and
 * nowhere in its documentation. For wants it is v2.4.3 (CHANGELOG.md at
 * 9054fa3): before 2.4.1 (lines 86-98) the wants export could write one line
 * per list and no cards, depending on the page's layout; before 2.4.3 (lines
 * 9-23) a want kept one language at most and every attribute came out `N`,
 * foil included — and `Language` and `IsFoil` are what
 * api/src/lib/wants-import.ts reads. The owner moved the
 * number to 2.4.3 on 2026-10-15. Why it matters differs by source, so the
 * reason after it is per source (`EXTENSION_VERSION_REASON_NAMES`).
 *
 * Where the export STARTS, and what the extension reads, belong to the source
 * (`EXPORT_STARTS_FROM_NAMES`, `EXTENSION_DISCLAIMER_NAMES`, beside
 * `IMPORT_SOURCE_FACTS`): the same extension makes both files, but not from
 * the same page. Everything else here holds for both.
 */
function HowToExport({ open, onClose, kind }) {
  const { t } = useApp();
  if (!open) return null;
  const p = { margin: 0, font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' };
  return (
    <ModalHost>
      <Dialog open closeLabel={t("Close")} width={520} onClose={onClose}
        title={t("Getting the file")}
        description={t("The export comes from a free browser extension. It is not ours.")}
        footer={<Button onClick={onClose}>{t("Got it")}</Button>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <p style={p}>{t(EXPORT_STARTS_FROM_NAMES[kind])}</p>
          <p style={p}><strong>{t("Use version 2.4.3 or later.")}</strong> {t(EXTENSION_VERSION_REASON_NAMES[kind])}</p>
          <p style={p}>{t(EXTENSION_DISCLAIMER_NAMES[kind])}</p>
          <p style={{ ...p, color: 'var(--text-strong)' }}>{t("We never see your Cardmarket account. You export a file and choose it here — no login of yours comes near us.")}</p>
          <a href="https://chromewebstore.google.com/detail/fdhioinnlcopijnekmkpfkopeplpgdkj"
            target="_blank" rel="noopener noreferrer"
            style={{ font: 'var(--type-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-link)' }}>
            {t("Open the Chrome Web Store listing")}
          </a>
          {/*
            The long version, on the marketing site, for somebody who wants the
            steps with screenshots' worth of detail rather than four sentences.
            Linked HERE because this dialog is where a person is actually stuck;
            a guide reachable only from a footer is a guide nobody finds at the
            moment they need it.
          */}
          <a href="https://cardboardmarket.xyz/exportar-cardmarket"
            target="_blank" rel="noopener noreferrer"
            style={{ font: 'var(--type-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-link)' }}>
            {t("Read the full guide")}
          </a>
        </div>
      </Dialog>
    </ModalHost>
  );
}

/**
 * WHERE THE FILE COMES FROM, on the step that asks for it.
 *
 * It lived under the two cards of the source picker, and a link that names its
 * source — the shop's Stock table, the Wants tab — skips the picker, so the
 * people arriving that way never read it: the one part of an import a
 * first-timer cannot guess. Shown only for a source whose file the extension
 * makes (`IMPORT_SOURCE_FACTS`), so a future source that needs no extension
 * says nothing about one.
 *
 * The dialog's open state is its own, here, and touches nothing of the step's:
 * closing it lands back on the same file, refusal and mode.
 */
function MadeByExtension({ kind }) {
  const { t } = useApp();
  const [how, setHow] = React.useState(false);
  return (
    <>
      <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
        {t("Made with a free browser extension that is not ours.")}{' '}
        <button type="button" onClick={() => setHow(true)}
          style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'var(--text-link)', cursor: 'pointer', textDecoration: 'underline' }}>
          {t("More information")}
        </button>
      </span>
      <HowToExport open={how} onClose={() => setHow(false)} kind={kind} />
    </>
  );
}

function SourcePicker({ onPick }) {
  const { t } = useApp();
  return (
    <div className="cbm-stack">
      {/*
        The question is HOW, not where.
        It asked "where is your inventory now?", which has one answer — Cardmarket —
        and would still have one answer if we read three different files from it.
        The cards underneath are mechanisms, so a "where" question could never be
        answered by picking one. Asking how also dissolves the collision the screen
        had with itself: an IMPORT screen whose only option was called an EXPORT.
      */}
      <span className="cbm-label">{t("How do you want to bring it in?")}</span>
      <Card cut interactive padding="var(--space-6)" onClick={() => onPick('stock')}
        style={{ display: 'flex', gap: 'var(--space-5)', alignItems: 'center', cursor: 'pointer' }}>
        <Icon name="layers" size={22} />
        <div style={{ flex: 1 }}>
          <div style={{ font: 'var(--type-h3)', color: 'var(--text-strong)' }}>{t(SOURCE_TITLE_NAMES[IMPORT_SOURCES.stock])}</div>
          <div style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
            {t("The file the extension generates. Quantity, condition, language and price come across.")}
          </div>
        </div>
        <Icon name="chevron-right" size={18} />
      </Card>
      {/*
        THE OTHER FILE THE SAME EXTENSION WRITES.
        
        Offered as its own choice rather than sniffed from whatever is uploaded.
        The two exports look almost identical in a downloads folder — same
        prefix, same date, same version — so somebody who picks the wrong one
        has made a mistake worth naming. Guessing would turn "you brought the
        other file" into silently importing the wrong thing, and a wants file
        read as stock would publish three hundred cards nobody owns.
      */}
      <Card cut interactive padding="var(--space-6)" onClick={() => onPick('wants')}
        style={{ display: 'flex', gap: 'var(--space-5)', alignItems: 'center', cursor: 'pointer' }}>
        <Icon name="heart" size={22} />
        <div style={{ flex: 1 }}>
          <div style={{ font: 'var(--type-h3)', color: 'var(--text-strong)' }}>{t(SOURCE_TITLE_NAMES[IMPORT_SOURCES.wants])}</div>
          <div style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
            {t("The cards you are looking for. The list each one is on comes across as a note.")}
          </div>
        </div>
        <Icon name="chevron-right" size={18} />
      </Card>
      {/*
        Where the file comes from is said on the step each card leads to — see
        `MadeByExtension` — and not here as well. A link that names its source
        skips this screen, so this was the one place guaranteed not to be read
        by those arrivals, and saying it on both would say it twice.
      */}
      <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
        {t("It is the only one we read today. Tell us what else you use and we will add it.")}
      </span>
    </div>
  );
}

/**
 * A big file going up the wire, while it goes.
 *
 * REPORTED: "the indication for the uploading is too small or at least it
 * should be horizontally centered". Both true, and the size was the symptom
 * rather than the fault — a 444 MB transfer was being reported in a line of
 * small text below a card about how to produce the file, next to a drop zone
 * still inviting a different one.
 *
 * Centred and given the room, because for the minutes this takes it IS the
 * screen. Three things, in the order somebody asks them: that it is sending,
 * how far it has got, and — the one a number alone cannot answer — whether it
 * is still moving.
 *
 * THE PERCENTAGE AND THE MEGABYTES BOTH, deliberately. The percentage is how
 * you judge whether to wait; the megabytes are how you tell that the number is
 * about YOUR file and not a generic bar. On a 444 MB upload "16%" alone reads
 * as a guess.
 */
function SendingFile({ file, sent }) {
  const { t } = useApp();
  const total = file ? file.size : 0;
  const pct = total ? Math.min(100, Math.round((sent / total) * 100)) : 0;

  return (
    <Card padding="var(--space-7)" style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 'var(--space-4)', textAlign: 'center',
    }}>
      <Icon name="loader" size={32} className="cbm-spin" style={{ color: 'var(--text-muted)' }} />

      <div>
        <div style={{ font: 'var(--type-h4)', color: 'var(--text-strong)' }}>
          {t("Sending your file…")}
        </div>
        {/* The name, because a big upload is worth being sure about. */}
        {file ? (
          <div style={{ font: 'var(--type-meta)', color: 'var(--text-muted)', marginTop: 'var(--space-1)' }}>
            {file.name}
          </div>
        ) : null}
      </div>

      {/*
        The bar gets the width of the card rather than the width of a sentence.
        `aria-hidden` because the numbers below are already announced, and a
        second live region saying the same thing interrupts twice.
      */}
      <div aria-hidden="true" style={{
        width: '100%', maxWidth: 420, height: 8, borderRadius: 4,
        background: 'var(--surface-sunken)', overflow: 'hidden',
      }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: 'var(--orange-600)', transition: 'width .2s linear',
        }} />
      </div>

      {/*
        THE UNIT FOLLOWS THE FILE. Rounding everything to whole megabytes read
        "0 of 0 MB sent. · 17%" on anything under half a megabyte, which is a
        sentence that undermines the number beside it. Production only ever
        shows this panel above 20 MB, but dev's threshold is 64 kB and a screen
        that talks nonsense in the environment we look at every day is a screen
        we stop reading.
      */}
      <div role="status" aria-live="polite" style={{ font: 'var(--type-body)', color: 'var(--text-body)' }}>
        {total >= 1024 * 1024
          ? t("{done} of {total} MB sent.", {
              done: Math.round(sent / (1024 * 1024)),
              total: Math.round(total / (1024 * 1024)),
            })
          : t("{done} of {total} KB sent.", {
              done: Math.round(sent / 1024),
              total: Math.round(total / 1024),
            })}
        {' · '}{pct}%
      </div>

      <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
        {t("A file this big goes up in pieces. Stay on this page until it is sent.")}
      </span>
    </Card>
  );
}

/**
 * An import that is still happening, and then what it did.
 *
 * ONE COMPONENT FOR BOTH STATES because they are one thing to the person
 * looking: "what happened to my file". Splitting them would mean the moment it
 * finishes is a different screen arriving, when what actually changes is a
 * sentence and a number.
 */
function QueuedImport({ job, kind, onAgain }) {
  const { t } = useApp();
  const running = job.status === 'pending' || job.status === 'running';
  const wants = kind === 'wants';

  /*
   * The failure a person can act on, told apart from the ones they cannot.
   *
   * The worker writes `not_a_wants_export` for the commonest mistake — the
   * other file — because the columns are only checkable by reading the file,
   * which the request deliberately does not do. Anything else is ours
   * (`import_failed`, `import_interrupted`) and is said as such rather than
   * shown as a code.
   */
  const wrongFile = job.error === 'not_a_wants_export' || job.error === 'not_a_stock_export';

  return (
    <Card padding="var(--space-6)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      {running ? (
        <>
          <span className="cbm-label">{t("Working on it")}</span>
          <p role="status" style={{ margin: 0, font: 'var(--type-body)', color: 'var(--text-body)' }}>
            {t("That file is big enough that we are reading it in the background. It will take a few minutes.")}
          </p>
          {/*
            HOW FAR IT HAS GOT.

            Asked for: a background import gave "we are reading it" and then
            nothing for minutes, which is indistinguishable from stuck. A number
            that moves is the difference between waiting and wondering.

            Rows READ, and it says so — a row can be read and then skipped, so
            this is not a count of what will be imported and must not be dressed
            up as one. The result, when it comes, is the other number.

            Only once there is one. Zero rows read is what the first few seconds
            of every import look like, and "0 rows" is worse than not saying.
          */}
          {job.rowsRead ? (
            <p role="status" aria-live="polite"
               style={{ margin: 0, font: 'var(--type-body)', color: 'var(--text-strong)' }}>
              {t("{n} rows read so far.", { n: job.rowsRead.toLocaleString() })}
            </p>
          ) : null}
          {/* The point of the whole path, said plainly. */}
          <p style={{ margin: 0, font: 'var(--type-body)', color: 'var(--text-strong)' }}>
            {t("You can leave this page. Come back to it and the result will be here.")}
          </p>
        </>
      ) : job.status === 'failed' ? (
        <>
          <span className="cbm-label">{t("It did not go through")}</span>
          <p role="alert" style={{ margin: 0, font: 'var(--type-body)', color: 'var(--status-danger)' }}>
            {wrongFile
              ? (wants
                ? t("That file is not a wants export. Go back and choose the stock export instead.")
                : t("That file is not a stock export. Go back and choose the wants list instead."))
              : t("Something went wrong at our end and nothing was imported. Nothing was changed.")}
          </p>
        </>
      ) : (
        <>
          <span className="cbm-label">{t("Done")}</span>
          <p style={{ margin: 0, font: 'var(--type-body)', color: 'var(--text-body)' }}>
            {wants
              ? t("{n} of {total} rows became wants.", { n: job.imported ?? 0, total: job.total ?? 0 })
              : t("{n} of {total} rows became listings.", { n: job.imported ?? 0, total: job.total ?? 0 })}
          </p>
          {job.failed ? (
            <ProblemRows problems={(job.problems || []).filter((p) => p.count)} />
          ) : null}
        </>
      )}
      {!running ? (
        <div className="cbm-actionbar">
          <Button variant="outline" onClick={onAgain}>{t("Import another file")}</Button>
        </div>
      ) : null}
    </Card>
  );
}

/*
 * THE TWO SOURCES, BY NAME, in one place.
 *
 * A link into the importer names its source with one of these (`?kind=`), and
 * the screen honours exactly these — "Importing wants" in AGENTS.md: its own
 * endpoint, its own source card, never sniffed. The back office's Stock table
 * once sent 'inventory', which nothing here recognised, so every shop landed on
 * the picker and chose stock again. Navigate with these, never a literal.
 */
export const IMPORT_SOURCES = Object.freeze({ stock: 'stock', wants: 'wants' });

/*
 * WHAT EACH SOURCE IS CALLED on screen: its picker card's title, and the back
 * button on its own step, read from here by both so the two cannot drift. The
 * back button once read the stock name on the wants step too. A `_NAMES` table
 * of English keys — a plain, unfrozen object literal — so
 * `check-translations.mjs` sees every value that reaches `t()`.
 */
const SOURCE_TITLE_NAMES = {
  /* The extension's own name, whole. "Exporter" inside a product name is not
     a verb telling anybody to export something, which is what the old
     "Cardmarket stock export" read as one line under "import". */
  [IMPORT_SOURCES.stock]: "Cardmarket Stock Exporter CSV",
  [IMPORT_SOURCES.wants]: "Cardmarket wants lists CSV",
};

/*
 * WHAT EACH SOURCE IS, beyond its name. Keyed by the words above, so the route
 * keeps using `IMPORT_SOURCES.stock` as a plain string.
 *
 * `madeByExtension`: the file is written by Cardmarket Stock Exporter, a
 * browser extension that is not ours, and the source's step says so with a
 * way to the how-to. Both Cardmarket exports come out of it today; a source
 * that does not would set it false and its step would say nothing about one.
 *
 * `columns`: what the server refuses the file without, shown on the source's
 * step under "What the file looks like" — so the wants step shows the wants
 * file's, not the stock header.
 */
const IMPORT_SOURCE_FACTS = Object.freeze({
  [IMPORT_SOURCES.stock]: Object.freeze({ madeByExtension: true, columns: EXPECTED_HEADER }),
  [IMPORT_SOURCES.wants]: Object.freeze({ madeByExtension: true, columns: EXPECTED_WANTS_COLUMNS }),
});

/*
 * WHAT EACH SOURCE'S FILE IS, and which other file it is not: the two
 * sentences around its `columns` in "What the file looks like" (`FileShape`).
 * `_NAMES` tables of English keys, plain and unfrozen, so
 * `check-translations.mjs` sees every value that reaches `t()`. The other file
 * is answered by name rather than failed row by row on both endpoints:
 * `not_a_stock_export` and `not_a_wants_export`.
 */
const FILE_SHAPE_IS_NAMES = {
  [IMPORT_SOURCES.stock]: "Your stock page on Cardmarket, exported as a semicolon-separated CSV. Whatever else it carries, it has to carry these:",
  [IMPORT_SOURCES.wants]: "Your want lists on Cardmarket, exported as a semicolon-separated CSV. Whatever else it carries, it has to carry these:",
};
const FILE_SHAPE_OTHER_NAMES = {
  [IMPORT_SOURCES.stock]: "Your wants are a different export with different columns. If that is the file that arrives, we say so instead of failing every row of it.",
  [IMPORT_SOURCES.wants]: "Your stock is a different export with different columns. If that is the file that arrives, we say so instead of failing every row of it.",
};

/*
 * WHERE EACH EXTENSION EXPORT STARTS, and what the extension reads to make it:
 * the how-to's two sentences that differ between the two sources. Facts of the
 * source like the ones above, kept as `_NAMES` tables of English keys — plain
 * object literals, unfrozen — so `check-translations.mjs` sees every value
 * that reaches the dialog through `t()`, as it does `REASON_NAMES`.
 *
 * Read out of the extension's own source (github.com/lupzn/cardmarket-stock-
 * exporter at 9054fa3, v2.4.3): the stock export pages through
 * `/Stock/Offers/Singles`, the stock page (popup.js 196); the wants export is
 * the "Wants" tab of its popup, button "Export all wantlists" (popup.html 181,
 * 383-389), which needs an open Cardmarket tab (popup.js 3266-3271) and
 * fetches `/{lang}/{game}/Wants` — the overview of your want lists — then each
 * list (popup.js 3498-3545), taking the language and game from that tab's
 * address (popup.js 3418-3420). PRIVACY.md 19 says the same of what it reads.
 */
const EXPORT_STARTS_FROM_NAMES = {
  [IMPORT_SOURCES.stock]: "Install Cardmarket Stock Exporter from the Chrome Web Store, open your stock page on Cardmarket, and export. The file lands in your downloads.",
  [IMPORT_SOURCES.wants]: "Install Cardmarket Stock Exporter from the Chrome Web Store, open your want lists on Cardmarket, and export them from the extension's Wants tab. The file lands in your downloads.",
};
const EXTENSION_DISCLAIMER_NAMES = {
  [IMPORT_SOURCES.stock]: "We did not write it, we do not maintain it, and we cannot support it. It is open source, and it says it sends nothing anywhere: it reads your stock page and saves a file to your computer.",
  [IMPORT_SOURCES.wants]: "We did not write it, we do not maintain it, and we cannot support it. It is open source, and it says it sends nothing anywhere: it reads your want lists and saves a file to your computer.",
};
/*
 * WHY THE MINIMUM VERSION, after the one number both sources share (see
 * `HowToExport`, which cites the changelog). Stock's is the damage an old
 * export does on Cardmarket; wants' is what an old export loses. "Can" for the
 * missing cards, because before 2.4.1 that depended on the page's layout.
 */
const EXTENSION_VERSION_REASON_NAMES = {
  [IMPORT_SOURCES.stock]: "Versions before 2.2.13 leave the language blank for some cards, and can write the wrong language back to your Cardmarket stock.",
  [IMPORT_SOURCES.wants]: "Older ones can leave the cards out of a want list, keep only one language per want, and lose its foil setting.",
};

export function ImportScreen() {
  const { loadMine, notify, route, t } = useApp();
  /*
   * WHICH of the two exports this is — 'stock' or 'wants'.
   *
   * Chosen at the first step rather than sniffed from the file, and that is
   * deliberate: the two files look almost identical in a downloads folder, so a
   * person who picks the wrong one has made a mistake worth telling them about
   * by name. Guessing would turn "you brought the other file" into a silent
   * import of the wrong thing.
   */
  /*
   * ARRIVED ALREADY DECIDED, where the link said so.
   *
   * The Wants tab has its own "Import your wants" button, and somebody who
   * pressed that has answered the first step by pressing it. Asking again would
   * be the screen not listening.
   *
   * Read once, as the initial value rather than in an effect, so the first
   * paint is already on the right step — an effect would show the source picker
   * for a frame and then replace it, which reads as a glitch.
   *
   * Anything else in the URL is ignored and the picker is shown, because the
   * two files look almost identical in a downloads folder and guessing which
   * one somebody has is the mistake step one exists to prevent.
   */
  const fromLink = Object.values(IMPORT_SOURCES).includes(route.kind) ? route.kind : null;
  const [kind, setKind] = React.useState(fromLink);
  /*
   * A link that named the kind has chosen the source too — there is only one,
   * and asking "which export?" after somebody pressed "Import your wants" is
   * the same question twice.
   */
  const [source, setSource] = React.useState(fromLink ? 'cardmarket' : null);
  const [file, setFile] = React.useState(null);
  /*
   * WHAT THE FILE IS ALLOWED TO MEAN, and `add` until somebody says otherwise.
   *
   * Asked for by a game store: keeping stock on two platforms is what decides
   * whether they list here at all. `sync` makes the file the inventory — live
   * cards take its prices, and anything missing from it comes down — which is
   * the opposite of the promise every other trader was given, so it is never
   * the default and never inferred from the file.
   *
   * The preview is what makes it safe to offer: it runs the whole thing and
   * rolls it back, so a shop sees how many of their cards this file would take
   * down BEFORE anything happens to them.
   */
  const [mode, setMode] = React.useState('add');
  const [busy, setBusy] = React.useState(false);
  /** What the import WOULD do, from a run the server rolled back. */
  const [preview, setPreview] = React.useState(null);
  /** What it DID. Once this exists it is the only thing shown. */
  const [report, setReport] = React.useState(null);
  /** A refusal the whole upload met, as a sentence. Not a per-row problem. */
  const [refusal, setRefusal] = React.useState(null);
  /*
   * BYTES OF THE FILE ACTUALLY SENT, while they are being sent.
   *
   * Only meaningful on the chunked path, which is the only path that can know:
   * a single `fetch` of a whole file reports nothing until it finishes, which
   * is why a stalled 444 MB upload looked exactly like a working one. A hundred
   * small requests can be counted.
   */
  const [uploaded, setUploaded] = React.useState(null);
  /*
   * A FILE TOO BIG TO READ WHILE SOMEBODY WATCHES.
   *
   * Past a threshold in bytes the server stores the file and answers 202 with a
   * job, because Traefik cuts a request at about sixty seconds and that — not
   * the work — is what stops a large import. This holds the job while it runs
   * and the answer once it has.
   */
  const [job, setJob] = React.useState(null);

  /*
   * PICK UP AN IMPORT ALREADY RUNNING, when the screen opens.
   *
   * Reported: a 62 MB file was queued, the screen said it would be read in the
   * background and that they could go and do something else — and going and
   * doing something else lost it. The id lived only here, so coming back
   * offered a fresh upload while the server was still reading the file.
   *
   * "You can leave this page" has to mean the page can be left AND come back.
   * The poll below already knows what to do with a job; it simply had no way to
   * learn about one it did not start.
   *
   * Once, on open. A trader who is watching an import does not need this again
   * — the poll has it from here — and a trader who has none pays one small
   * request for the screen to be honest.
   */
  React.useEffect(() => {
    let alive = true;
    api.currentImportJob()
      .then((r) => {
        if (!alive || !r.job) return;
        return api.importJob(r.job.id).then((full) => {
          if (!alive) return;
          setJob(full);
          /*
           * AND WHICH OF THE TWO IT WAS, from the job rather than from here.
           *
           * `kind` is chosen at the first step and lives in this screen's state,
           * so a recovered job arrived with it still null — and null is not
           * 'wants', it is 'stock' by every test on this screen. A recovered
           * wants import therefore reported that its rows "became listings",
           * under a heading offering to import an inventory. The job knows what
           * it is; nothing here had to guess.
           */
          setKind(full.kind === 'wants' ? 'wants' : 'stock');
        });
      })
      .catch(() => {
        // Not being able to ask is not the same as there being nothing, but the
        // screen still has to be usable: a failed check leaves the ordinary
        // upload in front of somebody rather than an error about a job that may
        // not exist.
      });
    return () => { alive = false; };
  }, []);

  /*
   * TELL THE SERVER IT HAS BEEN SEEN, once it is genuinely on screen.
   *
   * Reported: "the information about the file that has been imported is stale.
   * I already saw it before." A finished job was recovered on every visit for a
   * day and superseded the whole screen, so the last import kept introducing
   * itself as news.
   *
   * Sent from HERE — after the render that shows it — rather than from the
   * fetch, because the two make different claims. A fetch says the client
   * asked. This says a person was told, and only that should stop us telling
   * them again.
   *
   * It does not clear what is on screen. Whoever is reading it goes on reading
   * it for as long as they like; it is the NEXT visit that gets the uploader
   * back. Failure is ignored on purpose: the worst a lost mark can do is show a
   * result twice, and an error about bookkeeping in front of somebody reading
   * their import report would be the bigger fault.
   */
  React.useEffect(() => {
    if (!job || (job.status !== 'done' && job.status !== 'failed')) return;
    api.markImportJobSeen(job.id).catch(() => {});
  }, [job && job.id, job && job.status]);

  /*
   * WHERE THE LINE IS, asked of the server rather than copied into the bundle.
   *
   * Null until it answers, and a null line means "preview it" — the safe way
   * round: the fast path refuses what it cannot do, while guessing the other
   * way would send a file to a background job that did not need one.
   */
  const [limits, setLimits] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    api.importLimits().then((l) => { if (alive) setLimits(l); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const tooBigToPreview = (chosen) =>
    !!(limits && chosen && chosen.size > limits.backgroundOverBytes);

  /*
   * Asked every few seconds while it runs, and not once it has stopped.
   *
   * FIVE SECONDS rather than one: nothing on this screen changes faster than
   * the job does, and a background import is minutes. The interval is cleared
   * on the terminal states so a finished job does not keep a tab polling for
   * as long as it is open.
   */
  React.useEffect(() => {
    if (!job || job.status === 'done' || job.status === 'failed') return undefined;
    let alive = true;
    const ask = async () => {
      try {
        const fresh = await api.importJob(job.id);
        if (alive) setJob(fresh);
        // The Selling tab behind this is holding the list from before.
        if (fresh.status === 'done' && fresh.imported) loadMine();
      } catch {
        // A poll that fails is not a failed import: the next one may work, and
        // saying "your import failed" because one request did would be a lie.
      }
    };
    const timer = setInterval(ask, 5000);
    void ask();
    return () => { alive = false; clearInterval(timer); };
  }, [job && job.id, job && job.status]);

  /*
   * Everything the endpoint can refuse the WHOLE upload for.
   *
   * Handled by code rather than by message, because three of them carry a
   * number or a list that the generic table in api.jsx cannot interpolate —
   * and "something went wrong" in front of somebody holding the wrong file is
   * the answer that sends them away. Anything not named here falls through to
   * `e.message`, which the API client has already translated.
   */
  const refusalText = (e) => {
    const body = (e && e.payload) || {};
    if (e && e.code === 'not_a_stock_export') {
      return t("That is not a stock export — it has no {columns} column. It may be your wants file: go back and choose the wants list instead.",
        { columns: (body.missingColumns || []).join(', ') });
    }
    /*
     * The mirror of the refusal above, and it points the same way: back one
     * step, not at a different product. Both mistakes are somebody picking the
     * wrong file out of a downloads folder where the two look alike, and both
     * answers name the column that decided so the sentence is checkable rather
     * than just confident.
     */
    if (e && e.code === 'not_a_wants_export') {
      return t("That is not a wants export — it has no {columns} column. It may be your stock file: go back and choose the stock export instead.",
        { columns: (body.missingColumns || []).join(', ') });
    }
    if (e && e.code === 'file_too_large') {
      return t("That file is larger than {mb} MB.", { mb: Math.round((body.maxBytes || 0) / (1024 * 1024)) });
    }
    if (e && e.code === 'no_file') return t("No file arrived. Choose it and try again.");
    if (e && e.code === 'location_required') return t("Say where you trade from before importing — every listing needs it.");
    return (e && e.message) || t("That did not send. Try again in a moment.");
  };

  /**
   * The upload, in both of its two forms.
   *
   * One function because they are one call: the same file, the same endpoint,
   * the same answer shape, differing by a flag the server reads to decide
   * whether to COMMIT or ROLLBACK. Two functions here would have been two places
   * for the file, the refusal handling and the error copy to drift apart.
   */
  const send = async (chosen, dryRun) => {
    if (!chosen) return;
    setBusy(true);
    setRefusal(null);
    try {
      /*
       * IN PIECES WHENEVER IT IS NOT BEING PREVIEWED.
       *
       * A file that is going to be queued is a file we have already decided not
       * to read in front of anybody, and those are exactly the ones a single
       * request cannot carry: Cloudflare refuses a body at 100 MB — measured,
       * 95 MB arrives and 100 MB does not — and Traefik cuts at about sixty
       * seconds before that. The server has stored uploads as slices since
       * migration 094; this simply puts the browser on the other end of it.
       *
       * One path for every queued upload rather than a size test choosing
       * between two: a second threshold is a second edge for a file to fall
       * between, which is the mistake `backgroundThreshold` exists to stop
       * repeating. It also means the progress below is true of all of them.
       */
      const answer = !dryRun && tooBigToPreview(chosen)
        ? await api.uploadInParts({
            file: chosen, kind, mode,
            partBytes: limits && limits.uploadPartBytes,
            onProgress: (done) => setUploaded(done),
          })
        : kind === 'wants'
          ? await api.importCardmarketWants(chosen, { dryRun })
          : await api.importCardmarket(chosen, { dryRun, mode });
      /*
       * QUEUED, not done. There is no report to show and there will not be one
       * for a while, so the screen switches to waiting rather than rendering a
       * result made of nulls.
       */
      if (answer && answer.queued) {
        setJob({ id: answer.jobId, status: 'pending' });
      } else if (dryRun) {
        setPreview(answer);
      } else {
        /*
         * The result, and from here it is the only thing on screen.
         *
         * `preview` is kept rather than cleared, and only so that `ImportReport`
         * can say when the two disagreed. It is never rendered again.
         */
        setReport(answer);
        // The Selling tab behind this screen is holding the list from before the
        // import. Refreshed here rather than on arrival there, so going straight
        // to it shows what was just added.
        if (answer.imported) loadMine();
      }
    } catch (e) {
      /*
       * A refused preview leaves the file step; a refused commit stays on the
       * review, with the numbers it was about to act on still in front of it.
       * Neither invents a result: `report` is only ever set by a real answer.
       */
      if (dryRun) setPreview(null);
      setRefusal(refusalText(e));
      // Said twice on purpose: the toast is what somebody looking at the button
      // sees, the sentence below is what is still there a moment later.
      notify(refusalText(e), 'caution');
    } finally { setBusy(false); setUploaded(null); }
  };

  /*
   * Choosing a file previews it, straight away.
   *
   * There is no button between the two because there is nothing to decide yet:
   * a dry run writes nothing, so the only cost of looking is the upload itself,
   * and asking somebody to press "preview" before they can press "import" is a
   * click that buys them nothing. The decision this screen exists to protect is
   * the one after the numbers, not the one before them.
   */
  const choose = (chosen) => {
    setFile(chosen);
    // A new file means the previous answers are about a different file. Leaving
    // either on screen under a new filename is how somebody reads last time's
    // numbers as this time's.
    setPreview(null);
    setReport(null);
    setRefusal(null);
    /*
     * A FILE OVER THE LINE IS NOT PREVIEWED, it is sent.
     *
     * Previewing it would upload the whole thing to be refused by the fast
     * path's own cap — which is smaller than the background line, so every file
     * this path exists for would have been rejected on the way to reaching it.
     * That is the bug a browser test found: the screen offered a preview it
     * could never complete, and the confirm that would have queued the file sat
     * behind it.
     *
     * So the big ones go straight to the server, which stores them and answers
     * with a job. There is nothing to confirm — a preview is the import run and
     * rolled back, and nobody waits for a background job twice.
     */
    /*
     * REFUSED HERE, BEFORE A SINGLE BYTE LEAVES, when the file is over the
     * ceiling the server will refuse it at anyway.
     *
     * Reported from production with a 444 MB file: the screen sat on "Reading
     * your file…" for minutes with nothing moving, and no error ever arrived.
     * The upload was real — the whole file was going up the wire — to be
     * refused on arrival for being over 64 MB, and Traefik cuts a request at
     * about sixty seconds, so the refusal never came back either. The browser
     * was told nothing because nothing got that far.
     *
     * The size is known before the file is touched and the ceiling is already
     * on screen's own `limits`. Sending it anyway was asking somebody to spend
     * four hundred megabytes of their upload to be told a number we had.
     *
     * Said with the number, because "too large" with no figure leaves somebody
     * guessing which of their files might fit.
     */
    const ceiling = limits && limits.maxBackgroundBytes;
    if (ceiling && chosen.size > ceiling) {
      setRefusal(t("That file is {mb} MB. The largest we can take is {max} MB.", {
        mb: Math.round(chosen.size / (1024 * 1024)),
        max: Math.round(ceiling / (1024 * 1024)),
      }));
      return;
    }

    send(chosen, !tooBigToPreview(chosen));
  };

  /** Back to the file step, from either of the two steps that follow it. */
  const startOver = () => {
    setPreview(null);
    setReport(null);
    setRefusal(null);
    setFile(null);
    // The queued job too, or "Import another file" lands back on the one that
    // just finished — the branch that renders it is checked first.
    setJob(null);
  };

  return (
    <>
      <BackLink />
      {/*
        The heading follows the import too. The subtitle already switched — "The
        cards you are looking for" — under a title that said "Import your
        inventory", which is the opposite thing: an inventory is what you have,
        a wants list is what you do not.

        A comment cannot live between attributes inside a tag, which is what the
        first attempt did and what the build refused.
      */}
      <SectionHead
        title={kind === 'wants' ? t("Import your wants list") : t("Import your inventory")}
        sub={kind === 'wants'
          ? t("The cards you are looking for, brought over in one go. The list each one is on comes across as a note.")
          : t("A list you already keep somewhere else becomes listings here. Prices come across once and are never updated on their own.")}
      />
      {/*
        Where you are in the import, said out loud.

        Four steps, and the first one has a single option today — which is the
        argument for showing the rail rather than against it: it tells somebody
        that choosing a source IS a step, so the second source arriving later is
        an addition to something they already understand rather than a new shape.

        Review earns its place on the rail for a different reason: it says, before
        anybody is looking at numbers, that there is a step between the file and
        the result. Somebody who can see that the import has not happened yet does
        not have to be told twice.
      */}
      <div className="cbm-steps" style={{ marginTop: 'var(--space-6)' }}>
        {[t("Source"), t("File"), t("Review"), t("Result")].map((label, i) => {
          const n = i + 1;
          /*
             A recovered job has none of the local state the rail was reading —
             no source, no preview — so it drew step 01 above a finished result.
             The job says where it is: being read, or read.
          */
          const at = job
            ? (job.status === 'done' || job.status === 'failed' ? 4 : 3)
            : report ? 4 : preview ? 3 : source ? 2 : 1;
          return (
            <span key={label} data-on={at === n ? '1' : at > n ? 'done' : '0'}>
              {String(n).padStart(2, '0')} {label}
            </span>
          );
        })}
      </div>
      <div className="cbm-stack" style={{ maxWidth: 720 }}>
        {job ? (
          /*
           * A FILE TOO BIG TO WAIT FOR, and the screen says so rather than
           * spinning. It is checked FIRST because a queued job supersedes every
           * other state: there is no preview to confirm and no report yet.
           *
           * The honest promise here is "you can leave" — that is the whole
           * reason the slow path exists, and a screen that quietly required
           * somebody to stay would have given them the waiting back.
           */
          <QueuedImport job={job} kind={kind} onAgain={startOver} />
        ) : report ? (
          <ImportReport report={report} promised={preview} onAgain={startOver} kind={kind} />
        ) : preview ? (
          <>
            <div>
              {/* The file's own name, so the back link says which file is being
                  left behind rather than "back". */}
              <Button size="sm" variant="ghost" iconLeft="arrow-left" style={{ marginLeft: -10 }}
                disabled={busy} onClick={startOver}>
                {file ? file.name : t("Choose a different file")}
              </Button>
            </div>
            <ImportReport
            kind={kind}
              report={preview}
              preview
              busy={busy}
              onConfirm={() => send(file, false)}
              onAgain={startOver}
            />
            {/* A commit that was refused. The review it was refused from is
                still above, unchanged, because nothing about it stopped being
                true. */}
            {refusal ? (
              <p role="alert" style={{ margin: 0, font: 'var(--type-body)', color: 'var(--status-danger)' }}>{refusal}</p>
            ) : null}
          </>
        ) : !source ? (
          <SourcePicker onPick={(picked) => { setKind(picked); setSource('cardmarket'); }} />
        ) : (
          <>
            <div>
              <Button size="sm" variant="ghost" iconLeft="arrow-left" style={{ marginLeft: -10 }}
                onClick={() => { setSource(null); setKind(null); setFile(null); setRefusal(null); }}>
                {/* The import you are in, by the name its picker card carries. */}
                {t(SOURCE_TITLE_NAMES[kind])}
              </Button>
            </div>
            {/*
              SENDING IS NOT CHOOSING, so the screen stops being about choosing.

              Reported with a 444 MB upload: "the indication for the uploading
              is too small or at least it should be horizontally centered". It
              was both, but the size was the symptom. The progress sat as a
              footnote UNDER a card explaining what a Cardmarket export looks
              like, beside a drop zone still offering "Choose a different
              file" — the whole page laid out as though the choice were still
              ahead, while the only thing happening was the file going up.

              So the explainer and the drop zone step aside while it sends, the
              way the queued state already takes the screen when the file is
              being read. One thing at a time, and it is the thing that is
              happening.
            */}
            {uploaded === null ? (
              <>
                {/*
                  Only for stock, and only before anything is chosen is wrong —
                  it has to stay visible beside the file, because it changes
                  what pressing the button does. A wants file has nothing to
                  synchronise: a want is not stock and removing one is not a
                  sale.
                */}
                {kind === 'stock' ? (
                  <Select
                    label={t("What this file is")}
                    value={mode}
                    onChange={setMode}
                    options={[
                      { value: 'add', label: t("New cards to add to what I have") },
                      { value: 'sync', label: t("My whole inventory — keep it in step") },
                    ]}
                  />
                ) : null}
                <DropZone file={file} onFile={choose} disabled={busy} />
                {refusal ? (
                  <p role="alert" style={{ margin: 0, font: 'var(--type-body)', color: 'var(--status-danger)' }}>{refusal}</p>
                ) : null}
                {IMPORT_SOURCE_FACTS[kind]?.madeByExtension ? <MadeByExtension kind={kind} /> : null}
                <FileShape kind={kind} />
              </>
            ) : (
              <SendingFile file={file} sent={uploaded} />
            )}
            <div className="cbm-actionbar">
              {/*
                READING ONLY. Sending has its own panel above now — it takes the
                screen, because for the minutes a large upload lasts it IS the
                screen. Leaving the sending case here too rendered both at once.
              */}
              {busy && uploaded === null ? (
                /*
                 * WHAT IS TRUE, at the size it will be true at — the design
                 * system's own rule for a waiting state: "A placeholder is for
                 * something you cannot say yet; where you can say something
                 * true, say it."
                 *
                 * This was one line of unstyled text at the bottom of an empty
                 * screen, and a 19 MB file takes about fifteen seconds to read.
                 * Reported as needing better feedback, which it did: nothing
                 * said how big the job was, so a long wait and a hung page
                 * looked the same.
                 *
                 * No percentage, deliberately. The server reads the file in one
                 * pass and reports nothing until it is done, so a bar would be
                 * an animation pretending to be a measurement. The file's own
                 * size is a real number and sets a real expectation.
                 */
                <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <Icon name="loader" size={18} className="cbm-spin"
                        style={{ color: 'var(--text-muted)' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ font: 'var(--type-body)', color: 'var(--text-strong)' }}>
                      {/*
                        SENDING AND READING ARE DIFFERENT THINGS, and saying so
                        is the point.

                        Reported on a 444 MB file: "it has been loading for a
                        long time. I don't know if it is actually reading the
                        file or just uploading." It was uploading, and the
                        screen had no word for that — so somebody watching a
                        transfer that would never arrive was reading a sentence
                        about a stage it had not reached.
                      */}
                      {t("Reading your file…")}
                    </div>
                    <div style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
                      {file && file.size > 4 * 1024 * 1024
                        ? t("{mb} MB. A file this size takes a few seconds — stay on this page.",
                            { mb: Math.round(file.size / (1024 * 1024)) })
                        : t("This takes a moment.")}
                    </div>
                  </div>
                </div>
              ) : file && refusal ? (
                /* The one case where the file step needs a button: the preview
                   did not get through, and choosing the same file again is not
                   obviously a thing the drop zone will let you do. */
                <Button size="lg" onClick={() => send(file, true)}>{t("Try again")}</Button>
              ) : (
                /* Said rather than left to a disabled button, the same way the
                   posting form says which answer is still missing — and it says
                   what happens next, which is a look rather than an import. */
                <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {t("Choose your export and we will show you what it holds before anything is imported.")}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
